import { useState, useEffect } from 'react';
import { Modal, Table, Button, InputNumber, Form, Space, Spin, message, Typography, Tooltip } from 'antd';
import { DeleteOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { supabase } from '../lib/supabase';
import { formatPrice } from '../utils/format';

interface PriceTier {
  id: string;
  product_id: string;
  min_quantity: number;
  price: number;
}

interface TierPricingModalProps {
  productId: string;
  productName: string;
  basePrice: number;
  open: boolean;
  onClose: () => void;
}

export default function TierPricingModal({
  productId,
  productName,
  basePrice,
  open,
  onClose
}: TierPricingModalProps) {
  const [priceTiers, setPriceTiers] = useState<PriceTier[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (open && productId) {
      fetchPriceTiers();
    }
  }, [open, productId]);

  const fetchPriceTiers = async () => {
    if (!productId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('product_price_tiers')
        .select('*')
        .eq('product_id', productId)
        .order('min_quantity', { ascending: true });
      
      if (error) throw error;
      
      setPriceTiers(data || []);
    } catch (error: any) {
      console.error('Error fetching price tiers:', error);
      message.error('Fout bij het ophalen van prijsstaffels');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTier = () => {
    form.validateFields().then(values => {
      // Check if the quantity already exists
      if (priceTiers.some(tier => tier.min_quantity === values.min_quantity)) {
        message.error('Er bestaat al een staffel voor deze hoeveelheid');
        return;
      }

      // Add tier to database
      addTierToDatabase(values.min_quantity, values.price);
      
      // Reset form fields
      form.resetFields();
    });
  };

  const addTierToDatabase = async (minQuantity: number, price: number) => {
    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('product_price_tiers')
        .insert({
          product_id: productId,
          min_quantity: minQuantity,
          price: price
        })
        .select();
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        setPriceTiers([...priceTiers, data[0]]);
        message.success('Staffelprijs toegevoegd');
      }
    } catch (error: any) {
      console.error('Error adding price tier:', error);
      message.error('Fout bij het toevoegen van staffelprijs');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTier = async (tierId: string) => {
    try {
      const { error } = await supabase
        .from('product_price_tiers')
        .delete()
        .eq('id', tierId);
      
      if (error) throw error;
      
      setPriceTiers(priceTiers.filter(tier => tier.id !== tierId));
      message.success('Staffelprijs verwijderd');
    } catch (error: any) {
      console.error('Error deleting price tier:', error);
      message.error('Fout bij het verwijderen van staffelprijs');
    }
  };

  const columns = [
    {
      title: 'Vanaf aantal',
      dataIndex: 'min_quantity',
      key: 'min_quantity',
      sorter: (a: PriceTier, b: PriceTier) => a.min_quantity - b.min_quantity,
    },
    {
      title: 'Prijs per stuk',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => formatPrice(price),
    },
    {
      title: 'Korting',
      key: 'discount',
      render: (_: any, record: PriceTier) => {
        const discountPercentage = ((basePrice - record.price) / basePrice) * 100;
        return `${discountPercentage.toFixed(1)}%`;
      },
    },
    {
      title: 'Actie',
      key: 'action',
      render: (_: any, record: PriceTier) => (
        <Button 
          type="text" 
          danger 
          icon={<DeleteOutlined />} 
          onClick={() => handleDeleteTier(record.id)}
        />
      ),
    },
  ];

  return (
    <Modal
      title={`Staffelprijzen: ${productName}`}
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Sluiten
        </Button>
      ]}
      width={700}
    >
      <Spin spinning={loading}>
        <div style={{ marginBottom: 20 }}>
          <Typography.Text>
            Standaardprijs: <strong>{formatPrice(basePrice)}</strong>
          </Typography.Text>
          <Tooltip title="Voeg staffelprijzen toe om korting te geven bij grotere bestellingen">
            <QuestionCircleOutlined style={{ marginLeft: 8 }} />
          </Tooltip>
        </div>

        <Table
          dataSource={priceTiers}
          columns={columns}
          rowKey="id"
          pagination={false}
          style={{ marginBottom: 24 }}
        />

        <div style={{ border: '1px dashed #d9d9d9', padding: 16, borderRadius: 4 }}>
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            Nieuwe staffelprijs toevoegen
          </Typography.Title>
          
          <Form
            form={form}
            layout="inline"
            onFinish={handleAddTier}
          >
            <Form.Item
              name="min_quantity"
              label="Vanaf aantal"
              rules={[
                { required: true, message: 'Voer een minimum aantal in' },
                { type: 'number', min: 2, message: 'Aantal moet minimaal 2 zijn' }
              ]}
            >
              <InputNumber min={2} style={{ width: 120 }} />
            </Form.Item>
            
            <Form.Item
              name="price"
              label="Prijs per stuk"
              rules={[
                { required: true, message: 'Voer een prijs in' },
                { type: 'number', min: 0.01, message: 'Prijs moet groter dan 0 zijn' }
              ]}
              initialValue={basePrice * 0.9} // Default to 10% discount
            >
              <InputNumber 
                min={0.01} 
                step={0.01} 
                style={{ width: 120 }} 
                precision={2}
                formatter={(value) => `€ ${value}`}
                parser={(value) => {
                  const parsed = parseFloat(value?.replace(/[^\d.]/g, '') || '0');
                  return isNaN(parsed) ? 0.01 : parsed;
                }}
              />
            </Form.Item>
            
            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<PlusOutlined />}
                loading={saving}
              >
                Toevoegen
              </Button>
            </Form.Item>
          </Form>
        </div>
      </Spin>
    </Modal>
  );
} 